import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Backdrop,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Paper,
  Container,
  Fade,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DataTable from './Datagrid';
import { END_POINTS, ALERTS } from './Constants';
import { CandidateContext } from './Context';
import axiosInstance from '../Api/Instance';

const { GET_CANDIDATES, SEARCH_CANDIDATES, CANDIDATE } = END_POINTS;

function Candidatelist() {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [data, setData] = useState({ candidates: [], pagination: {} });
  const [openLoader, setOpenLoader] = useState(false);
  const { applicant_status } = React.useContext(CandidateContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // searchQuery  — what the user typed in the search bar (visible)
  // statusFilter — what the user selected from the dropdown (never written into the search bar)
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchController = useRef(null);

  // One-time initialization from URL
  useEffect(() => {
    const p = Number(searchParams.get('page'))   || 1;
    const l = Number(searchParams.get('limit'))  || 10;
    const q = searchParams.get('query')          || '';
    const s = searchParams.get('status')         || '';
    setPage(p);
    setLimit(l);
    setSearchQuery(q);
    setStatusFilter(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state → URL
  useEffect(() => {
    setSearchParams((prev) => {
      const currentParams = new URLSearchParams(prev);
      const currentPage   = Number(currentParams.get('page'))  || 1;
      const currentLimit  = Number(currentParams.get('limit')) || 10;
      const currentQuery  = currentParams.get('query')         || '';
      const currentStatus = currentParams.get('status')        || '';

      if (
        currentPage   === page         &&
        currentLimit  === limit        &&
        currentQuery  === searchQuery  &&
        currentStatus === statusFilter
      ) return prev;

      const newParams = new URLSearchParams();
      newParams.set('page',  page);
      newParams.set('limit', limit);
      if (searchQuery)  newParams.set('query',  searchQuery);
      if (statusFilter) newParams.set('status', statusFilter);
      return newParams;
    });
  }, [page, limit, searchQuery, statusFilter, setSearchParams]);

  // Fetch whenever any filter changes
  useEffect(() => {
    fetchCandidates();
    return () => {
      if (fetchController.current) fetchController.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, searchQuery, statusFilter]);

 const fetchCandidates = async () => {
  if (fetchController.current) fetchController.current.abort();
  fetchController.current = new AbortController();

  try {
    setLoader(true);
    const params = new URLSearchParams({ page, limit });

    // Send search query separately
    if (searchQuery) params.append('query', searchQuery);

    // Send dropdown status separately
    if (statusFilter) params.append('status', statusFilter);

    const url = searchQuery || statusFilter
      ? `${SEARCH_CANDIDATES}?${params.toString()}`
      : `${GET_CANDIDATES}?${params.toString()}`;

    const res = await axiosInstance.get(url, {
      signal: fetchController.current.signal,
    });

    setData(res.data);
  } catch (err) {
    if (err.name === 'CanceledError' || err.name === 'AbortError') return;
    console.error('error fetching candidates:', err);
  } finally {
    setLoader(false);
  }
};

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  // Status dropdown — never touches the search bar
  const handleStatusChange = (status) => {
    setStatusFilter((prev) => (prev === status ? '' : status));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    const updatedPage = newPage + 1;
    setPage(updatedPage);
    const params = { page: updatedPage, limit };
    if (searchQuery)  params.query  = searchQuery;
    if (statusFilter) params.status = statusFilter;
    setSearchParams(params);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => {
      const params = new URLSearchParams();
      params.set('limit', newLimit);
      params.set('page', 1);
      if (searchQuery)  params.set('query',  searchQuery);
      if (statusFilter) params.set('status', statusFilter);
      return params;
    });
  };

  const handleDeleteSelected = async (selectedIds) => {
    if (!window.confirm('Are you sure you want to delete the selected profiles?')) return;
    try {
      await axiosInstance.post('/candidatelist/multi-delete', {
        selectedCandidates: selectedIds,
      });
      fetchCandidates();
    } catch (err) {
      console.error('error deleting profiles:', err);
      alert('Failed to delete profiles. Please refresh and try again.');
    }
  };

  const handleExport = () => {
    if (!data.candidates?.length) return;
    const csvContent = [
      ['Name', 'Email', 'Applied Position', 'Status', 'Experience', 'Location'].join(','),
      ...data.candidates.map((c) =>
        [c.name, c.email, c.applied_position, c.status, `${c.experience} yrs`, c.location].join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'candidates.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSingleProfile = async (candidate) => {
    try {
      setOpenLoader(true);
      const res = await axiosInstance.get(`${CANDIDATE}/${candidate.id}`);
      navigate(`${CANDIDATE}/${candidate.id}`, { state: { candidate: res.data } });
    } catch (err) {
      console.error(`${ALERTS.HTTP_ERROR}:`, err.response?.status || err.message);
    } finally {
      setOpenLoader(false);
    }
  };

  const handleCloseLoader = () => setOpenLoader(false);

  const StatusChip = ({ label, list }) => {
    const match = list?.find((item) => item.label === label);
    const bgColor = match?.color || '#E5E7EB';
    const getTextColor = (bg) => {
      const c = bg.substring(1);
      const rgb = parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 160 ? '#1F2937' : '#FFFFFF';
    };
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: getTextColor(bgColor),
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          border: `1px solid ${bgColor}20`,
          borderRadius: 1.5,
          textTransform: 'capitalize',
        }}
      />
    );
  };

  const columns = [
    {
      field: 'serial',
      headerName: 'S.No.',
      width: 30,
      renderCell: (params, index) => (
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
          {index + 1}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Candidate Name',
      width: 100,
      renderCell: (params) => {
        const name = params.name || '';
        const truncated = name.length > 15 ? `${name.slice(0, 15)}…` : name;
        return (
          <Tooltip title={name} arrow>
            <Typography
              variant="body2"
              component="a"
              href={`${CANDIDATE}/${params.id}`}
              onClick={(e) => {
                e.preventDefault();
                if (e.button === 0) handleSingleProfile(params);
              }}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: '#1d4ed8',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
                '&:hover': { textDecoration: 'underline', color: '#1e40af' },
              }}
            >
              {truncated}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email Address',
      width: 220,
      renderCell: (params) => {
        const email = params.email || '';
        const truncated = email.length > 15 ? `${email.slice(0, 15)}…` : email;
        return (
          <Tooltip title={email} arrow>
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#4b5563',
                display: 'block',
                maxWidth: 200,
              }}
            >
              {truncated}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'applied_position',
      headerName: 'Applied Role',
      width: 180,
      renderCell: (params) => {
        const role = params.applied_position || '';
        const truncated = role.length > 15 ? `${role.slice(0, 15)}…` : role;
        return (
          <Tooltip title={role} arrow>
            <Box
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => handleSingleProfile(params)}
            >
              <WorkIcon sx={{ fontSize: 16, color: '#7e22ce' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 130,
                }}
              >
                {truncated}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Box onClick={() => handleSingleProfile(params)}>
          <StatusChip label={params.status} list={applicant_status} />
        </Box>
      ),
    },
    {
      field: 'experience',
      headerName: 'Experience',
      width: 110,
      renderCell: (params) => (
        <Chip
          icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
          label={`${params.experience} yrs`}
          size="small"
          sx={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            fontWeight: 600,
            fontSize: 11,
            height: 24,
          }}
        />
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      renderCell: (params) => {
        const location = params.location || '';
        const truncated = location.length > 10 ? `${location.slice(0, 10)}…` : location;
        return (
          <Tooltip title={location} arrow>
            <Box className="flex items-center gap-1.5">
              <LocationOnIcon sx={{ fontSize: 16, color: '#ef4444' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 110,
                }}
              >
                {truncated}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const candidates = data.candidates || [];
  const rows = candidates.map((details) => ({
    id:               details?._id,
    name:             details?.name,
    email:            details?.email,
    applied_position: details?.applied_position,
    status:           details?.status,
    experience:       details?.experience,
    location:         details?.location,
  }));

  const totalCandidates   = data.pagination?.total_records || 0;
  const activeFilterCount = (searchQuery ? 1 : 0) + (statusFilter ? 1 : 0);

  return (
    <Box className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Container maxWidth="xl" className="py-6 px-4 md:px-6">

        {/* Profile loading backdrop */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}
          open={openLoader}
          onClick={handleCloseLoader}
        >
          <Box className="flex flex-col items-center gap-4">
            <CircularProgress size={60} thickness={4} sx={{ color: '#60a5fa' }} />
            <Typography variant="body1" className="font-medium">
              Loading candidate profile...
            </Typography>
          </Box>
        </Backdrop>

        {/* Page header */}
        <Fade in timeout={600}>
          <Box className="mb-8">
            <Box className="flex items-center gap-3 mb-3">
              <Box className="w-1.5 h-12 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full shadow-md" />
              <Box>
                <Typography
                  variant="h4"
                  className="font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent"
                >
                  Candidate Management
                </Typography>
                <Typography variant="body2" className="text-gray-600 mt-1">
                  Manage and review all candidate applications in one place
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* Table card */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            className="rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
            sx={{ background: 'linear-gradient(to bottom, #ffffff 0%, #f9fafb 100%)' }}
          >
            {/* Card header */}
            <Box className="p-6 border-b border-gray-200 bg-white">
              <Box className="flex items-center justify-between flex-wrap gap-3">
                <Box className="flex items-center gap-3">
                  <Box className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
                    <PeopleIcon sx={{ color: '#fff', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" className="font-bold text-gray-900">
                      All Candidates
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {totalCandidates} total candidates
                      {statusFilter && ` · status: "${statusFilter}"`}
                      {searchQuery  && ` · searching "${searchQuery}"`}
                    </Typography>
                  </Box>
                </Box>

                {/* Clear-all badge — only visible when filters are active */}
                {activeFilterCount > 0 && (
                  <Chip
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`}
                    size="small"
                    onDelete={() => {
                      setStatusFilter('');
                      setSearchQuery('');
                      setPage(1);
                    }}
                    sx={{
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      fontWeight: 600,
                      fontSize: 12,
                      border: '1px solid #bfdbfe',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* DataTable — status dropdown lives inside DataTable's toolbar */}
            <Box className="p-6">
              <DataTable
                columns={columns}
                rows={rows}
                data={data}
                loading={loader}
                searchValue={searchQuery}
                rowsPerPageProp={limit}
                currentPage={page - 1}
                statusFilter={statusFilter}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                onExport={handleExport}
                onDeleteSelected={handleDeleteSelected}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onStatusChange={handleStatusChange}
              />
            </Box>
          </Paper>
        </Fade>

      </Container>
    </Box>
  );
}

export default Candidatelist;